import { useState, useEffect } from 'react'
import { 
  Users, 
  Calendar, 
  MessageCircle, 
  TrendingUp, 
  Clock,
  AlertCircle,
  CheckCircle,
  Loader2
} from 'lucide-react'
import { useCoachStore } from '@/stores/coachStore'
import { apiService } from '@/services/api'

interface DashboardData {
  todaysFocus: {
    pendingCheckIns: Array<{ id: string; name: string; lastActivity: string }>
    upcomingSessions: Array<{ 
      id: string; 
      clientName: string; 
      time: string; 
      type: string;
      location: string;
    }>
    newMessages: Array<{ id: string; from: string; message: string; time: string }>
    urgentItems: Array<{ id: string; type: string; clientName: string; description: string }>
  }
  quickStats: {
    totalActiveClients: number
    weeklyActiveSessions: number
    averageClientRating: number
    responseTime: number
    newThisWeek: number
    needingAttention: number
  }
  recentActivity: Array<{ id: string; type: string; clientName: string; description: string; timestamp: string }>
}

export function DashboardPage() {
  const { coach, setCurrentPage } = useCoachStore()
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadDashboardData = async () => {
    try {
      setIsLoading(true)
      setError(null)
      const response = await apiService.getDashboardStats()
      setDashboardData(response.data as DashboardData)
    } catch (err: any) {
      console.error('Failed to load dashboard data:', err)
      setError('Failed to load dashboard data')
      // Fallback to basic empty structure so UI doesn't break
      setDashboardData({
        todaysFocus: {
          pendingCheckIns: [],
          upcomingSessions: [],
          newMessages: [],
          urgentItems: []
        },
        quickStats: {
          totalActiveClients: 0,
          weeklyActiveSessions: 0,
          averageClientRating: 0,
          responseTime: 0,
          newThisWeek: 0,
          needingAttention: 0
        },
        recentActivity: []
      })
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    setCurrentPage('dashboard')
    loadDashboardData()
  }, [setCurrentPage])

  // Show loading spinner
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center space-x-2">
          <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
          <span className="text-gray-600">Loading dashboard data...</span>
        </div>
      </div>
    )
  }

  // Show error state  
  if (error && !dashboardData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Failed to load dashboard</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={loadDashboardData}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  if (!dashboardData) {
    return null
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="coach-header">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Good morning, {coach?.name?.split(' ')[0] || 'Coach'}! 👋
          </h1>
          <p className="text-gray-600">
            Here's what's happening with your clients today
          </p>
          {error && (
            <div className="mt-2 text-sm text-amber-600 flex items-center">
              <AlertCircle className="w-4 h-4 mr-1" />
              Using cached data - {error}
            </div>
          )}
        </div>
      </div>

      {/* Quick Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="coach-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Active Clients</p>
              <p className="text-2xl font-bold text-gray-900">
                {dashboardData.quickStats.totalActiveClients}
              </p>
              <p className="text-sm text-success-600">
                +{dashboardData.quickStats.newThisWeek} this week
              </p>
            </div>
            <div className="p-3 bg-primary-100 rounded-lg">
              <Users className="w-6 h-6 text-primary-600" />
            </div>
          </div>
        </div>

        <div className="coach-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Weekly Sessions</p>
              <p className="text-2xl font-bold text-gray-900">
                {dashboardData.quickStats.weeklyActiveSessions}
              </p>
              <p className="text-sm text-gray-600">This week</p>
            </div>
            <div className="p-3 bg-success-100 rounded-lg">
              <Calendar className="w-6 h-6 text-success-600" />
            </div>
          </div>
        </div>

        <div className="coach-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Client Rating</p>
              <p className="text-2xl font-bold text-gray-900">
                {dashboardData.quickStats.averageClientRating}
              </p>
              <p className="text-sm text-gray-600">Average score</p>
            </div>
            <div className="p-3 bg-warning-100 rounded-lg">
              <TrendingUp className="w-6 h-6 text-warning-600" />
            </div>
          </div>
        </div>

        <div className="coach-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Response Time</p>
              <p className="text-2xl font-bold text-gray-900">
                {dashboardData.quickStats.responseTime}h
              </p>
              <p className="text-sm text-gray-600">Average</p>
            </div>
            <div className="p-3 bg-danger-100 rounded-lg">
              <Clock className="w-6 h-6 text-danger-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Today's Focus Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Urgent Items */}
        {dashboardData.todaysFocus.urgentItems.length > 0 && (
          <div className="coach-section">
            <div className="flex items-center space-x-2 mb-4">
              <AlertCircle className="w-5 h-5 text-danger-600" />
              <h2 className="text-lg font-semibold text-gray-900">Urgent Items</h2>
            </div>
            <div className="space-y-3">
              {dashboardData.todaysFocus.urgentItems.map((item) => (
                <div key={item.id} className="p-3 bg-danger-50 border border-danger-200 rounded-lg">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium text-danger-900">{item.clientName}</p>
                      <p className="text-sm text-danger-700">{item.description}</p>
                    </div>
                    <button className="text-sm text-danger-600 hover:text-danger-800 font-medium">
                      Address
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pending Check-ins */}
        <div className="coach-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Pending Check-ins</h2>
            <span className="status-warning">{dashboardData.todaysFocus.pendingCheckIns.length}</span>
          </div>
          <div className="space-y-3">
            {dashboardData.todaysFocus.pendingCheckIns.map((client) => (
              <div key={client.id} className="flex items-center justify-between p-3 bg-warning-50 border border-warning-200 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">{client.name}</p>
                  <p className="text-sm text-gray-600">Last activity: {client.lastActivity}</p>
                </div>
                <button className="coach-button-secondary text-sm px-3 py-1">
                  Send Reminder
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Today's Sessions */}
        <div className="coach-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Today's Sessions</h2>
            <span className="status-active">{dashboardData.todaysFocus.upcomingSessions.length}</span>
          </div>
          <div className="space-y-3">
            {dashboardData.todaysFocus.upcomingSessions.map((session) => (
              <div key={session.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">{session.clientName}</p>
                  <p className="text-sm text-gray-600">{session.time} • {session.type}</p>
                  <p className="text-xs text-gray-500">{session.location}</p>
                </div>
                <div className="flex space-x-2">
                  <button className="coach-button-secondary text-sm px-3 py-1">
                    Prep
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Messages */}
        <div className="coach-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Recent Messages</h2>
            <MessageCircle className="w-5 h-5 text-gray-400" />
          </div>
          <div className="space-y-3">
            {dashboardData.todaysFocus.newMessages?.length > 0 ? (
              dashboardData.todaysFocus.newMessages.map((message) => (
                <div key={message.id} className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{message.from}</p>
                      <p className="text-sm text-gray-600 line-clamp-2">{message.message}</p>
                    </div>
                    <span className="text-xs text-gray-500 whitespace-nowrap ml-2">{message.time}</span>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-500 text-center py-4">No new messages</p>
            )}
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="coach-section">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h2>
        <div className="space-y-3">
          {dashboardData.recentActivity.map((activity) => (
            <div key={activity.id} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
              <div className="flex-shrink-0">
                <CheckCircle className="w-5 h-5 text-success-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm">
                  <span className="font-medium text-gray-900">{activity.clientName}</span>
                  <span className="text-gray-600"> {activity.description}</span>
                </p>
                <p className="text-xs text-gray-500">{activity.timestamp}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}